import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useTokens,
  usePropertyField,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { getContrastTextColor } from '../utils/luminance';
import { formatBedroomsList } from '../utils/formatBedroomsList';

/** Non-breaking space for richtext defaults — keeps glue words with what follows. */
const NBSP = '&nbsp;';

const CITY =
  '<span data-type="field-token" data-field="property_address_city"></span>';
const STATE =
  '<span data-type="field-token" data-field="property_address_state"></span>';
const CITY_STATE = `${CITY},${NBSP}${STATE}`;

/**
 * Per-page default subtitles keyed by `tokenPrefix` from pages.json.
 * Each entry uses property field tokens so copy stays dynamic per site.
 */
const DEFAULT_SUBTITLES: Record<string, string> = {
  'interactive-map-hero':
    `<p>Apartment Site Map in${NBSP}${CITY_STATE}</p>`,
  'virtual-tour-hero':
    `<p>Virtual Apartment Tours in${NBSP}${CITY_STATE}</p>`,
  'amenities-hero':
    `<p>Apartment Amenities in${NBSP}${CITY_STATE}</p>`,
  'pet-policy-hero':
    `<p>Pet-Friendly Apartments in${NBSP}${CITY_STATE}</p>`,
  'gallery-hero':
    `<p>Apartment Photos in${NBSP}${CITY_STATE}</p>`,
  'neighborhood-hero':
    `<p>The Neighborhood in${NBSP}${CITY_STATE}</p>`,
  'contact-hero':
    `<p>Contact Our ${CITY_STATE} Team</p>`,
  'resident-access-hero':
    `<p>Resident Portal in${NBSP}${CITY_STATE}</p>`,
  'schedule-tour-hero':
    `<p>Schedule a Tour in${NBSP}${CITY_STATE}</p>`,
  'reviews-hero':
    `<p>Resident Reviews in${NBSP}${CITY_STATE}</p>`,
  'privacy-policy-hero':
    `<p>Privacy Policy</p>`,
  'sitemap-hero':
    `<p>Sitemap</p>`,
  'accessibility-hero':
    `<p>Accessibility Statement</p>`,
  'apply-now-hero':
    `<p>Apply for Apartments in${NBSP}${CITY_STATE}</p>`,
  'thank-you-hero':
    `<p>Thank You for Your Interest</p>`,
  'faq-hero':
    `<p>Answers About Our${NBSP}${CITY_STATE} Community</p>`,
};

interface HeroSolidBGProps {
  tokenPrefix?: string;
  title?: string;
  subtitle?: string;
  ctaLabel?: string;
  ctaUrl?: string;
}

export function HeroSolidBG({
  tokenPrefix,
  title = 'Page',
  subtitle,
  ctaLabel = 'View Floor Plans',
  ctaUrl = '/floor-plans',
}: HeroSolidBGProps) {
  const prefix = tokenPrefix || 'herosolidbg';
  const titlePath = `${prefix}.title`;
  const subtitlePath = `${prefix}.subtitle`;
  const ctaPath = `${prefix}.cta_link`;

  const bedroomsRange = usePropertyField('property_bedrooms_range');

  const subtitleDefault = useMemo(() => {
    if (subtitle) return subtitle;

    if (prefix === 'floor-plans-hero') {
      const bedsList = formatBedroomsList(bedroomsRange as string | null)
        .replace(/ & /g, `${NBSP}& `);
      const bedsPhrase = bedsList ? `${bedsList}-Bedroom ` : '';
      return `<p>${bedsPhrase}Apartments in${NBSP}${CITY_STATE}</p>`;
    }

    return DEFAULT_SUBTITLES[prefix] || '';
  }, [subtitle, prefix, bedroomsRange]);

  // The default URL tracks the default CTA label so the link target matches
  // the verb ("View Floor Plans" → /floor-plans). Each page in pages.json
  // is expected to pass `ctaLabel` and `ctaUrl` together when overriding;
  // property_apply_now_url / property_website_url remain available to
  // editors via suggestedFields.
  const ctaLinkDef: SlotGroupDefinition = {
    label: 'CTA Link',
    fields: {
      url: {
        label: 'URL',
        type: 'url',
        defaultValue: ctaUrl,
        suggestedFields: ['property_apply_now_url', 'property_website_url'],
      },
      label: {
        label: 'Link Text',
        type: 'text',
        defaultValue: ctaLabel,
      },
      _target: {
        label: 'Target',
        type: 'text',
        defaultValue: '_self',
      },
    },
  };

  const cta = useSlotGroup(ctaPath, ctaLinkDef);

  // Pick a readable text/border color for the CTA on top of the primary
  // background. Mirrors the Footer's `getContrastTextColor` so the CTA stays
  // in lockstep with `--vibe-color-text-on-primary` (which the tokens runtime
  // sets via the same threshold). The earlier `pickHighestContrast` between
  // `textOnPrimary` and `textOnSecondary` was technically more "WCAG-optimal"
  // but produced black-on-medium-tone primaries (gold, tan, dusty blue) where
  // the desired visual answer — and the runtime's own text-on-primary token —
  // is white. The shared helper applies the documented 0.55 luminance
  // threshold that favors white on those medium tones.
  const tokens = useTokens();
  const onSolidColor = useMemo(() => {
    const primary = tokens?.color?.primary;
    if (!primary) return null;
    return getContrastTextColor(primary);
  }, [tokens]);

  return (
    <section
      className="bg-primary text-primary-foreground pb-14 md:pb-20"
      style={{
        paddingTop: 'calc(var(--offer-banner-height, 0px) + 5rem)',
        transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        // Drives `.hero-solid-cta-btn` below. The CSS rules fall back to white
        // when this var is absent (e.g. tokens haven't hydrated yet) so the
        // visual is identical to the previous hardcoded behavior.
        ...(onSolidColor ? { ['--rise-hero-solid-text' as string]: onSolidColor } : {}),
      }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-6 items-center pt-6 md:pt-4 lg:pt-16">

          {/* Subtitle + Page Title */}
          <div className="min-w-0 w-full">
            <RichTextContent
              path={subtitlePath}
              defaultValue={subtitleDefault}
              as="h2"
              className="font-heading text-sm sm:text-base md:text-lg font-normal uppercase tracking-wider leading-snug break-words text-center text-pretty opacity-90"
            />
            <RichTextContent
              path={titlePath}
              defaultValue={`<p>${title}</p>`}
              as="h1"
              className="font-heading text-2xl sm:text-[1.75rem] md:text-4xl lg:text-[2.625rem] xl:text-5xl font-bold uppercase tracking-wider leading-tight break-words text-center mt-3 md:mt-4"
            />
          </div>

          {/* CTA */}
          <div className="text-center">
            <EditableSlotGroup groupPath={ctaPath} definition={ctaLinkDef}>
              <a
                href={cta.url || '#'}
                target={cta._target || '_self'}
                className="hero-solid-cta-btn group/cta relative inline-flex items-center gap-2 px-8 py-3 font-heading text-sm uppercase tracking-wider overflow-hidden transition-all duration-300"
              >
                <span className="relative z-10">{cta.label || ctaLabel}</span>
              </a>
            </EditableSlotGroup>
          </div>

        </div>
      </div>

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent
        path="herosolidbg.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.subtitle"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="herosolidbg.cta_link"
        definition={ctaLinkDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        /* Outline CTA on the solid-primary hero. Resting border/label track
           --rise-hero-solid-text (luminance-picked for contrast on primary).
           Hover fills with secondary and flips the label to text-on-secondary
           — same treatment as .tabbed-cta / .nav-cta-btn. */
        .hero-solid-cta-btn {
          background: transparent;
          border: 1.5px solid color-mix(in srgb, var(--rise-hero-solid-text, #fff) 75%, transparent);
          border-radius: 2px;
          color: var(--rise-hero-solid-text, #fff);
          box-shadow: none;
        }
        .hero-solid-cta-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: var(--vibe-color-secondary);
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .hero-solid-cta-btn:hover {
          border-color: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
          transform: translateY(-1px);
          box-shadow:
            0 2px 6px rgba(0,0,0,0.15),
            0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 25%, transparent);
        }
        .hero-solid-cta-btn:hover::before {
          opacity: 1;
        }
        .hero-solid-cta-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HeroSolidBG',
  displayName: 'Hero – Solid Background',
  tokens: {
    'herosolidbg.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
    'herosolidbg.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'herosolidbg.cta_link': {
      label: 'CTA Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/floor-plans',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'View Floor Plans',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
  },
};

(HeroSolidBG as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HeroSolidBG;
